import { SfxToggle, useSfx } from '@slingu/ui';
import { useRef, useState } from 'react';

import { commitScore, getHighScore } from './highscores.js';
import { LEGACY_POOL } from './vocabulary.js';

const HIGH_SCORE_ID = 'word-mines';

export type WordMinesProps = {
  onExit: (result: { score: number; rounds: number; xp: number }) => void;
};

type Phase = 'menu' | 'playing' | 'over';

const GRID_COLS = 5;
const GRID_ROWS = 5;
const TOTAL_TILES = GRID_COLS * GRID_ROWS;
const MINES = 7;    // 💣 visible tiles
const ROUNDS_PER_GAME = 6;

type TileKind = 'word' | 'mine';
type TileState = 'hidden' | 'correct' | 'wrong-word' | 'exploded';
type Tile = { kind: TileKind; word: string; state: TileState };

/**
 * Word Mines — campo minado de palavras. 5×5 grid; alguns tiles mostram
 * palavras em inglês, outros mostram 💣. Toque na tradução correta da
 * palavra em português pra ganhar XP. Bater numa mina = explosão -2♥;
 * tocar numa palavra errada = -1♥. 3 vidas totais.
 */
export function WordMines({ onExit }: WordMinesProps) {
  const sfx = useSfx();
  const sfxRef = useRef(sfx);
  sfxRef.current = sfx;

  const [phase, setPhase] = useState<Phase>('menu');
  const [tiles, setTiles] = useState<Tile[]>([]);
  const [targetWord, setTargetWord] = useState<string>('');
  const [promptPt, setPromptPt] = useState<string>('');
  const [hearts, setHearts] = useState(3);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState<number>(() => getHighScore(HIGH_SCORE_ID));
  const [newRecord, setNewRecord] = useState<boolean>(false);

  // Helper — sempre que o jogo termina, registra o recorde antes de mostrar
  // a tela "over". Mais simples que duplicar essa lógica em 4 callsites.
  function finishGame() {
    const result = commitScore(HIGH_SCORE_ID, score);
    setHighScore(result.best);
    setNewRecord(result.isNew);
    setPhase('over');
  }
  const [round, setRound] = useState(0);
  const [lock, setLock] = useState(false);
  const [shake, setShake] = useState(false);
  const [usedIdxs, setUsedIdxs] = useState<number[]>([]);

  function shuffle<T>(arr: T[]): T[] {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j]!, a[i]!];
    }
    return a;
  }

  function newRound(nextRound: number, usedSoFar: number[]) {
    const pool = LEGACY_POOL.filter((_, i) => !usedSoFar.includes(i));
    if (pool.length < 1) {
      finishGame();
      return;
    }
    // Pick target pair
    const targetIdxInPool = Math.floor(Math.random() * pool.length);
    const targetPair = pool[targetIdxInPool]!;
    const originalIdx = LEGACY_POOL.findIndex((p) => p === targetPair);

    // Build tiles: 1 correct + (TOTAL - MINES - 1) distractors + MINES mines
    const distractors = shuffle(LEGACY_POOL.filter((p) => p !== targetPair))
      .slice(0, TOTAL_TILES - MINES - 1)
      .map((p) => p.correct);

    const words: Tile[] = [
      { kind: 'word', word: targetPair.correct, state: 'hidden' },
      ...distractors.map((w) => ({ kind: 'word' as const, word: w, state: 'hidden' as const })),
    ];
    const mines: Tile[] = Array.from({ length: MINES }, () => ({
      kind: 'mine' as const,
      word: '💣',
      state: 'hidden' as const,
    }));
    const deck = shuffle([...words, ...mines]);

    setTargetWord(targetPair.correct.toUpperCase());
    setPromptPt(targetPair.promptPt ?? targetPair.correct);
    setTiles(deck);
    setLock(false);
    setRound(nextRound);
    setUsedIdxs([...usedSoFar, originalIdx]);
  }

  function startGame() {
    setScore(0);
    setHearts(3);
    newRound(1, []);
    setPhase('playing');
    sfxRef.current.play('start');
  }

  function onTileTap(idx: number) {
    if (lock) return;
    const tile = tiles[idx];
    if (!tile) return;
    if (tile.state !== 'hidden') return;

    if (tile.kind === 'mine') {
      // EXPLODE: -2 hearts
      setLock(true);
      setShake(true);
      setTiles((prev) =>
        prev.map((t, i) => (i === idx ? { ...t, state: 'exploded' } : t)),
      );
      sfxRef.current.play('hit');
      sfxRef.current.play('heart-loss');
      sfxRef.current.play('game-over');
      const newHearts = Math.max(0, hearts - 2);
      setHearts(newHearts);
      window.setTimeout(() => setShake(false), 400);
      window.setTimeout(() => {
        if (newHearts <= 0) {
          finishGame();
        } else {
          advance();
        }
      }, 1100);
      return;
    }

    // Word tile — check match
    if (tile.word.toUpperCase() === targetWord) {
      setLock(true);
      setTiles((prev) =>
        prev.map((t, i) => (i === idx ? { ...t, state: 'correct' } : t)),
      );
      sfxRef.current.play('xp-gain');
      setScore((s) => s + 15);
      window.setTimeout(advance, 800);
    } else {
      // Wrong word — reveal it, -1 heart, continue same round (can still try)
      setTiles((prev) =>
        prev.map((t, i) => (i === idx ? { ...t, state: 'wrong-word' } : t)),
      );
      sfxRef.current.play('heart-loss');
      const newHearts = hearts - 1;
      setHearts(newHearts);
      if (newHearts <= 0) {
        setLock(true);
        window.setTimeout(finishGame, 900);
      }
    }
  }

  function advance() {
    const next = round + 1;
    if (next > ROUNDS_PER_GAME) {
      finishGame();
      return;
    }
    newRound(next, usedIdxs);
  }

  return (
    <div className="sl-minigame sl-minigame--portrait">
      <button
        type="button"
        className="sl-minigame__close"
        onClick={() => onExit({ score, rounds: round, xp: score })}
        aria-label="Fechar jogo"
      >
        ×
      </button>
      <div className="sl-flappy__sfx"><SfxToggle variant="icon" /></div>

      {phase === 'menu' ? (
        <div className="sl-flappy__menu">
          <h2><span aria-hidden>💣</span> Word Mines</h2>
          <p>
            5×5 tiles. Ache a tradução certa da palavra no topo.{' '}
            <strong style={{ color: 'rgb(238,85,85)' }}>Mina = -2♥</strong>,{' '}
            palavra errada = -1♥. 6 rodadas, 3 corações. Palavras sorteadas do
            pool de 1000+.
          </p>
          {highScore > 0 ? (
            <p className="sl-flappy__record">
              🏆 Seu recorde: <strong>{highScore} XP</strong>
            </p>
          ) : null}
          <div className="sl-flappy__menu-actions">
            <button type="button" className="sl-btn sl-btn--primary sl-btn--lg sl-btn--upper"
              onClick={() => { setNewRecord(false); startGame(); }}>Começar</button>
          </div>
        </div>
      ) : null}

      {phase === 'playing' ? (
        <div className={`sl-wmines${shake ? ' sl-wmines--shake' : ''}`}>
          <div className="sl-wbuilder__hud">
            <span>Rodada {round}/{ROUNDS_PER_GAME}</span>
            <span>♥ {hearts}</span>
            <span className="sl-wbuilder__hud-item--xp">{score} XP</span>
          </div>
          <div className="sl-wbuilder__prompt">
            <span className="sl-wbuilder__prompt-eyebrow">TOQUE NA TRADUÇÃO DE</span>
            <p className="sl-wbuilder__prompt-text">{promptPt}</p>
          </div>
          <div
            className="sl-wmines__grid"
            style={{ gridTemplateColumns: `repeat(${GRID_COLS}, 1fr)` }}
          >
            {tiles.map((tile, i) => (
              <button
                key={i}
                type="button"
                className={`sl-wmines__tile sl-wmines__tile--${tile.kind} sl-wmines__tile--${tile.state}`}
                onClick={() => onTileTap(i)}
                disabled={tile.state !== 'hidden' || lock}
              >
                <span className="sl-wmines__word">
                  {tile.kind === 'mine' ? '💣' : tile.word}
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {phase === 'over' ? (
        <div className="sl-flappy__over">
          <h2>{newRecord ? '🏆 Novo recorde!' : hearts > 0 ? 'Sobreviveu!' : 'Explodiu geral.'}</h2>
          <p>
            Rodadas: <strong>{round}</strong>/{ROUNDS_PER_GAME}<br />
            XP: <strong>{score}</strong>
          </p>
          <p className="sl-flappy__record">
            🏆 Recorde: <strong>{highScore} XP</strong>
            {newRecord ? ' ✨' : null}
          </p>
          <button type="button" className="sl-btn sl-btn--primary sl-btn--lg sl-btn--upper"
            onClick={() => onExit({ score, rounds: round, xp: score })}
          >Voltar pro app</button>
        </div>
      ) : null}
    </div>
  );
}
